/**
 * Content-addressed SED identity (hash scheme v2).
 *
 * A full rebuild ALWAYS mints a new sedId and bumps sedVersion, and restamps
 * per-run provenance (retrievedAt / fetchedAt / amendment-mirror ids), even
 * when every piece of evidence it produced is identical. Decision-analysis
 * currency keyed on those volatile fields therefore invalidated every
 * persisted analysis on every rebuild (the 2026 Redhead re-key storm: 55
 * rebuilds Jun–Aug, "Withheld · 0 of N decision-current").
 *
 * This module defines what the SED's CONTENT is for identity purposes: the
 * document minus rebuild identity, caller-stamped caches, and provenance
 * timestamps. Two SEDs with equal content hashes carry the same decision
 * evidence, regardless of when or under which sedId they were compiled.
 *
 * Exclusion rules (deliberately conservative — over-stripping risks a MISSED
 * change / false-current, under-stripping only risks a spurious refresh):
 *
 * 1. TOP-LEVEL identity + derived-cache keys (exact names): `id`, `version`,
 *    `collectedAt`, `builtAt`, `builtForIntentHash` (intent is separately
 *    bound + hashed on the analysis envelope), `sectionFingerprints`
 *    (client-stamped incremental-rebuild cache), `amendments` (in-band
 *    reapply AUDIT rows whose ids embed `--reapplied-<epochMs>`; the amended
 *    VALUES are folded into section data, which stays in the hash) and
 *    `evidenceConflicts` (conflict rows back-point at volatile amendment ids;
 *    a genuine provider shift also changes section values, which the hash
 *    sees).
 * 2. RECURSIVE provenance stamps: any key ending in the camelCase suffix
 *    `At` (retrievedAt, fetchedAt, parsedAt, analyzedAt, appliedAt, …) plus
 *    `lastUpdated`. These are when-was-this-obtained bookkeeping, not
 *    evidence. Evidence-bearing dates in this codebase are nouns
 *    (`inServiceDate`, `cod`, timeline fields) and are NOT stripped.
 *    ⚠️ When adding a NEW SED field whose *value* is decision evidence, do
 *    not name it with the `At` suffix — it would be excluded from identity.
 */
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import {
  REPORT_TYPE_SED_CURRENCY_BINDINGS,
  REPORT_TYPE_SED_REQUIREMENTS,
} from '../../config/reportTypeSedRequirements';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { projectSiteEvidenceForPrompt } from '../sed/reportPromptProjection';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  reportSedMaterialDependencyManifestV1Schema,
  type ReportSedMaterialDependencyManifestV1,
} from '../../types/decisionAnalysis';

export const REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION = 'report-sed-material-dependencies-v1' as const;

// Exact known acquisition/cache keys. Evidence dates such as observedAt,
// effectiveDate, cod and inServiceDate remain, as do unknown extension keys.
const MATERIAL_PROVENANCE_KEYS = new Set([
  'retrievedAt', 'fetchedAt', 'parsedAt', 'analyzedAt', 'lastAttemptedAt',
  'lastUpdated', 'bodyCacheHits', 'ocrCacheHits',
]);
const MATERIAL_TOP_LEVEL_KEYS = new Set([
  'id', 'version', 'contentHash', 'collectedAt', 'builtAt', 'builtForIntentHash',
  'sectionFingerprints',
]);

function materialValue(value: unknown, parentKey: string | null): unknown {
  if (value === null || typeof value !== 'object') return value ?? null;
  if (Array.isArray(value)) return value.map(entry => materialValue(entry, parentKey));
  const out: Record<string, unknown> = Object.create(null);
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (entry === undefined || MATERIAL_PROVENANCE_KEYS.has(key)) continue;
    if (key === 'cached' && typeof entry === 'boolean') continue;
    if (parentKey === 'carriedForward' && key === 'at') continue;
    // Amendment IDs are regenerated during reapply; the field, old/new
    // value, author and reason actually shown to the report remain bound.
    if (parentKey === 'amendments' && ['id', 'correctedAt', 'appliedAt'].includes(key)) continue;
    out[key] = key === 'source' && typeof entry === 'string'
      ? entry.replace(/\s*\(cached\)\s*$/i, '')
      : materialValue(entry, key);
  }
  return out;
}

function hasExplicitlyEmptySourcePayload(source: Record<string, unknown>): boolean {
  // Current uploaded-document payloads can be truncated and lack complete
  // body hashes. They cannot replace the opaque collection identity. Only
  // an explicitly empty source set proves that no document content was seen.
  return Array.isArray(source.uploadedDocuments)
    && source.uploadedDocuments.length === 0
    && Array.isArray(source.documentFacts)
    && source.documentFacts.length === 0
    && Array.isArray(source.generationDocumentEvidence)
    && source.generationDocumentEvidence.length === 0
    && Object.prototype.hasOwnProperty.call(source, 'siteContext') && source.siteContext === null;
}

/** Bind the actual generation projection, including every universal field,
 * full selected document evidence, unknown extensions and absence states.
 * No relevance inference, numeric tolerance or LLM judgement is used. */
export function buildReportSedMaterialDependencyManifest(
  sedDocument: unknown,
  reportType: string,
  options: { fullDocument?: boolean } = {},
): ReportSedMaterialDependencyManifestV1 {
  if (!Object.prototype.hasOwnProperty.call(REPORT_TYPE_REGISTRY, reportType)) {
    throw new Error('Unknown report type cannot declare material SED dependencies.');
  }
  if (sedDocument === null || typeof sedDocument !== 'object' || Array.isArray(sedDocument)) {
    throw new Error('Material SED dependencies require the complete source object.');
  }
  const scope = options.fullDocument ? 'full-document' : 'report-prompt';
  // Match immutable JSON encoding (including Dates), reject cycles and leave
  // the source untouched before applying the actual generation projection.
  const jsonSource: unknown = JSON.parse(canonicalJson(sedDocument));
  const source = (options.fullDocument ? jsonSource : projectSiteEvidenceForPrompt(
    jsonSource as SiteEvidenceDocument, reportType as ReportTypeId,
  )) as unknown as Record<string, unknown>;
  const emptySources = hasExplicitlyEmptySourcePayload(source);
  const fieldHashes: Record<string, string> = Object.create(null);
  for (const key of Object.keys(source).sort()) {
    if (MATERIAL_TOP_LEVEL_KEYS.has(key) || source[key] === undefined) continue;
    if (key === 'documentInputHash' && emptySources) continue;
    const value = key === 'publicSentiment'
      ? publicSentimentEvidenceProjection(source[key]) : source[key];
    // Prefixing also prevents strict-schema implementations treating a
    // source extension named __proto__ as an object prototype setter.
    fieldHashes[`sed.${key}`] = hashCanonicalJson(materialValue(value, key));
  }
  const payload = { schemaVersion: REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION,
    reportType, scope, fieldHashes };
  return reportSedMaterialDependencyManifestV1Schema.parse({
    ...payload, contentHash: hashCanonicalJson(payload),
  });
}

export function verifyReportSedMaterialDependencyManifest(
  value: unknown,
  reportType?: string,
): value is ReportSedMaterialDependencyManifestV1 {
  const parsed = reportSedMaterialDependencyManifestV1Schema.safeParse(value);
  if (!parsed.success || !Object.prototype.hasOwnProperty.call(REPORT_TYPE_REGISTRY, parsed.data.reportType)
    || (reportType !== undefined && parsed.data.reportType !== reportType)) return false;
  const { contentHash, ...payload } = parsed.data;
  return contentHash === hashCanonicalJson(payload);
}

export function reportSedMaterialDependencyDrift(
  frozen: unknown,
  live: unknown,
): { current: boolean; changedFields: string[] } {
  if (!verifyReportSedMaterialDependencyManifest(frozen)
    || !verifyReportSedMaterialDependencyManifest(live)
    || frozen.reportType !== live.reportType || frozen.scope !== live.scope) {
    return { current: false, changedFields: [] };
  }
  const changedFields = [...new Set([...Object.keys(frozen.fieldHashes), ...Object.keys(live.fieldHashes)])]
    .filter(key => frozen.fieldHashes[key] !== live.fieldHashes[key]).sort();
  return { current: changedFields.length === 0, changedFields };
}

export interface SedReportMaterialHashMirror {
  scheme: typeof REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION;
  sedContentHash: string;
  hashesByType: Record<string, string>;
  fieldHashesByType?: Record<string, Record<string, string>>;
  /** Storage codec only; reconstructed manifest/hash semantics remain v1. */
  fieldHashEncoding?: 'entry-index-v1' | 'entry-index-v2';
  fieldHashEntries?: [string, string][] | Array<{ field: string; hash: string }>;
  fieldEntryIndicesByType?: Record<string, number[]>;
}

export function reportSedMaterialHashMirrorKey(reportType: string,
  scope: ReportSedMaterialDependencyManifestV1['scope']): string {
  return scope === 'full-document' ? `full-document:${reportType}` : reportType;
}

const MATERIAL_MIRROR_MAX_ENTRIES = 8_192;
const MATERIAL_MIRROR_MAX_FIELDS_PER_TYPE = 2_048;
const own = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function materialMirrorKeys(): string[] {
  return Object.keys(REPORT_TYPE_REGISTRY).flatMap(type => [type, `full-document:${type}`]).sort();
}

function completeMirrorFieldsValid(
  mirror: Record<string, unknown>, fields: Record<string, Record<string, string>>,
): boolean {
  if (mirror.scheme !== REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION
    || typeof mirror.sedContentHash !== 'string' || !/^[a-f0-9]{64}$/.test(mirror.sedContentHash)
    || !record(mirror.hashesByType)) return false;
  const hashesByType = mirror.hashesByType;
  const keys = materialMirrorKeys();
  if (canonicalJson(Object.keys(hashesByType).sort()) !== canonicalJson(keys)
    || canonicalJson(Object.keys(fields).sort()) !== canonicalJson(keys)) return false;
  return keys.every(key => {
    const fieldHashes = fields[key];
    if (!record(fieldHashes) || Object.keys(fieldHashes).length > MATERIAL_MIRROR_MAX_FIELDS_PER_TYPE
      || Object.keys(fieldHashes).some(field => !field.startsWith('sed.') || field.length <= 4)) return false;
    const full = key.startsWith('full-document:');
    return verifyReportSedMaterialDependencyManifest({
      schemaVersion: mirror.scheme, reportType: full ? key.slice('full-document:'.length) : key,
      scope: full ? 'full-document' : 'report-prompt', contentHash: hashesByType[key], fieldHashes,
    });
  });
}

/** Resolve only storage encoding. Legacy field proofs keep their existing
 * per-manifest validation; encoded proofs require complete, unambiguous maps
 * and every original scoped manifest hash before any map can be consumed. */
export function resolveSedReportMaterialFieldHashes(
  value: unknown,
): Record<string, Record<string, string>> | undefined {
  if (!record(value)) return undefined;
  if (!own(value, 'fieldHashEncoding')) {
    if (own(value, 'fieldHashEntries') || own(value, 'fieldEntryIndicesByType')) return undefined;
    return record(value.fieldHashesByType)
      ? value.fieldHashesByType as Record<string, Record<string, string>> : undefined;
  }
  if ((value.fieldHashEncoding !== 'entry-index-v1' && value.fieldHashEncoding !== 'entry-index-v2')
    || own(value, 'fieldHashesByType')
    || !Array.isArray(value.fieldHashEntries) || value.fieldHashEntries.length > MATERIAL_MIRROR_MAX_ENTRIES
    || !record(value.fieldEntryIndicesByType)) return undefined;
  const entries: [string, string][] = [];
  const pairs = new Set<string>();
  for (const entry of value.fieldHashEntries) {
    // v1 tuples remain readable, but v2 uses map entries: Firestore cannot
    // persist an array directly inside another array.
    const pair = value.fieldHashEncoding === 'entry-index-v1' ? entry
      : record(entry) && Object.keys(entry).length === 2 && own(entry, 'field') && own(entry, 'hash')
        ? [entry.field, entry.hash] : undefined;
    if (!Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== 'string'
      || !pair[0].startsWith('sed.') || pair[0].length <= 4
      || typeof pair[1] !== 'string' || !/^[a-f0-9]{64}$/.test(pair[1])) return undefined;
    const key = canonicalJson(pair);
    if (pairs.has(key)) return undefined;
    pairs.add(key);
    entries.push(pair as [string, string]);
  }
  const fields: Record<string, Record<string, string>> = Object.create(null);
  const used = new Set<number>();
  for (const [key, indexes] of Object.entries(value.fieldEntryIndicesByType)) {
    if (!Array.isArray(indexes) || indexes.length > MATERIAL_MIRROR_MAX_FIELDS_PER_TYPE) return undefined;
    const map: Record<string, string> = Object.create(null);
    for (const index of indexes) {
      if (!Number.isSafeInteger(index) || index < 0 || index >= entries.length) return undefined;
      const [field, hash] = entries[index] as [string, string];
      if (own(map, field)) return undefined;
      map[field] = hash;
      used.add(index);
    }
    fields[key] = map;
  }
  if (used.size !== entries.length || !completeMirrorFieldsValid(value, fields)) return undefined;
  return fields;
}

/** Pure lossless storage compaction, suitable for a separately authorized CAS
 * attachment. Invalid/incomplete proofs are never repaired or inferred.
 * Return legacy storage if normalization offers no reduction; no source SED,
 * field manifest, content hash, evidence or other site field is modified. */
export function compactSedReportMaterialHashMirror(value: unknown): SedReportMaterialHashMirror | undefined {
  if (!record(value)) return undefined;
  const fields = resolveSedReportMaterialFieldHashes(value);
  if (!fields || !completeMirrorFieldsValid(value, fields)) return undefined;
  if (value.fieldHashEncoding === 'entry-index-v2') return value as unknown as SedReportMaterialHashMirror;
  const entries: Array<{ field: string; hash: string }> = [];
  const pairIndexes = new Map<string, number>();
  const byType: Record<string, number[]> = Object.create(null);
  for (const key of materialMirrorKeys()) {
    byType[key] = [];
    for (const field of Object.keys(fields[key]).sort()) {
      const pair: [string, string] = [field, fields[key][field]];
      const identity = canonicalJson(pair);
      let index = pairIndexes.get(identity);
      if (index === undefined) {
        index = entries.length; entries.push({ field: pair[0], hash: pair[1] }); pairIndexes.set(identity, index);
      }
      byType[key].push(index);
    }
  }
  const { fieldHashesByType: _legacy, fieldHashEncoding: _encoding,
    fieldHashEntries: _entries, fieldEntryIndicesByType: _indices, ...rest } = value;
  const compact = { ...rest, fieldHashEncoding: 'entry-index-v2' as const,
    fieldHashEntries: entries, fieldEntryIndicesByType: byType } as unknown as SedReportMaterialHashMirror;
  const decoded = resolveSedReportMaterialFieldHashes(compact);
  if (!decoded || canonicalJson(decoded) !== canonicalJson(fields)) return undefined;
  // Never return validated v1 tuples to a writer, even if normalizing them
  // costs a few bytes. Unencoded legacy maps are already Firestore-safe.
  return value.fieldHashEncoding === 'entry-index-v1'
    || new TextEncoder().encode(JSON.stringify(compact)).byteLength
    < new TextEncoder().encode(JSON.stringify(value)).byteLength
    ? compact : value as unknown as SedReportMaterialHashMirror;
}

export function computeSedReportMaterialHashMirror(sedDocument: unknown): SedReportMaterialHashMirror {
  const manifests = Object.keys(REPORT_TYPE_REGISTRY).flatMap(reportType =>
    [false, true].map(fullDocument => {
      const manifest = buildReportSedMaterialDependencyManifest(sedDocument, reportType, { fullDocument });
      return [reportSedMaterialHashMirrorKey(reportType, manifest.scope), manifest] as const;
    }));
  const legacy: SedReportMaterialHashMirror = { scheme: REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION,
    sedContentHash: computeSedContentHashV2(sedDocument),
    hashesByType: Object.fromEntries(manifests.map(([key, manifest]) => [key, manifest.contentHash])),
    fieldHashesByType: Object.fromEntries(manifests.map(([key, manifest]) => [key, manifest.fieldHashes])),
  };
  return compactSedReportMaterialHashMirror(legacy) ?? legacy;
}

/** A mirror only certifies the same full source and collection revision with
 * which it was atomically written. Missing/stale/wrong-scheme maps prove none. */
export function validSedReportMaterialHashMirror(site: {
  sedContentHash?: string;
  sedContentHashVersion?: number;
  sedVersion?: number;
  sedReportMaterialHashes?: unknown;
}): Readonly<Record<string, string>> | undefined {
  const row = site.sedReportMaterialHashes;
  if (!row || typeof row !== 'object' || Array.isArray(row)) return undefined;
  const mirror = row as Partial<SedReportMaterialHashMirror>;
  if (mirror.scheme !== REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION
    || !site.sedContentHash || !Number.isSafeInteger(site.sedVersion) || (site.sedVersion ?? 0) < 1
    || site.sedContentHashVersion !== site.sedVersion
    || mirror.sedContentHash !== site.sedContentHash
    || !mirror.hashesByType || typeof mirror.hashesByType !== 'object' || Array.isArray(mirror.hashesByType)
    || Object.values(mirror.hashesByType).some(hash => typeof hash !== 'string' || !/^[a-f0-9]{64}$/.test(hash))) {
    return undefined;
  }
  if ((own(mirror, 'fieldHashEncoding') || own(mirror, 'fieldHashEntries')
    || own(mirror, 'fieldEntryIndicesByType')) && !resolveSedReportMaterialFieldHashes(mirror)) return undefined;
  return mirror.hashesByType;
}

/** Field-level explanations are trusted only when they reconstruct the exact
 * scoped hash already certified by the fresh full-source mirror. */
export function reportSedMaterialDependencyManifestAtMirror(
  site: Parameters<typeof validSedReportMaterialHashMirror>[0],
  reportType: string,
  scope: ReportSedMaterialDependencyManifestV1['scope'],
): ReportSedMaterialDependencyManifestV1 | undefined {
  const hashes = validSedReportMaterialHashMirror(site);
  if (!hashes || !Object.prototype.hasOwnProperty.call(REPORT_TYPE_REGISTRY, reportType)
    || (scope !== 'report-prompt' && scope !== 'full-document')) return undefined;
  const key = reportSedMaterialHashMirrorKey(reportType, scope);
  const fields = resolveSedReportMaterialFieldHashes(site.sedReportMaterialHashes);
  if (!Object.prototype.hasOwnProperty.call(hashes, key) || !fields || typeof fields !== 'object'
    || Array.isArray(fields) || !Object.prototype.hasOwnProperty.call(fields, key)) return undefined;
  const fieldHashes = fields[key];
  if (!fieldHashes || typeof fieldHashes !== 'object' || Array.isArray(fieldHashes)
    || Object.keys(fieldHashes).some(field => !field.startsWith('sed.') || field.length <= 4)) return undefined;
  const manifest = { schemaVersion: REPORT_SED_MATERIAL_DEPENDENCY_SCHEMA_VERSION,
    reportType, scope, contentHash: hashes[key], fieldHashes };
  return verifyReportSedMaterialDependencyManifest(manifest, reportType) ? manifest : undefined;
}

const TOP_LEVEL_EXCLUDED_KEYS = new Set([
  'id',
  'version',
  'collectedAt',
  'builtAt',
  'builtForIntentHash',
  'sectionFingerprints',
  'amendments',
  'evidenceConflicts',
  'contentHash',
]);

const RECURSIVE_EXCLUDED_KEYS = new Set(['lastUpdated']);

/** Shared source ledgers survive every per-report prompt projection. */
export const SHARED_DOCUMENT_SED_INPUTS = ['documentFacts', 'generationDocumentEvidence', 'documentInputHash', 'refdataAcceptance'] as const;

function isProvenanceTimestampKey(key: string): boolean {
  if (RECURSIVE_EXCLUDED_KEYS.has(key)) return true;
  // camelCase `…At` suffix — at least one lowercase char before it, so a
  // hypothetical field literally named "At" (or an acronym like "VAT") is
  // kept.
  return key.length > 2 && key.endsWith('At') && /[a-z0-9]/.test(key[key.length - 3]);
}

function stripVolatile(value: unknown, topLevel: boolean): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((entry) => stripVolatile(entry, false));
  }
  const source = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    if (topLevel && TOP_LEVEL_EXCLUDED_KEYS.has(key)) continue;
    if (isProvenanceTimestampKey(key)) continue;
    result[key] = stripVolatile(source[key], false);
  }
  return result;
}

/**
 * The normalized identity payload — exported for tests and debugging so the
 * exclusion behaviour is directly assertable.
 */
export function sedContentIdentityPayload(sedDocument: unknown): unknown {
  if (sedDocument === null || typeof sedDocument !== 'object' || Array.isArray(sedDocument)) {
    return sedDocument ?? null;
  }
  return stripVolatile(sedDocument, true);
}

/** SHA-256 of the canonical normalized payload (hash scheme v2). */
export function computeSedContentHashV2(sedDocument: unknown): string {
  return hashCanonicalJson(sedContentIdentityPayload(sedDocument));
}

/**
 * Per-SECTION content hashes (hash scheme v2 normalization, section scope).
 *
 * Whole-document hashing made concurrent analysis runs mutually destructive:
 * every completing report's SED writeback changed the WHOLE-document hash, so
 * every in-flight analysis read stale at commit even when the writeback
 * touched nothing that analysis consumed (live: Redhead 2026-08-10, three
 * parallel retries each held the others stale). Staleness must mean "an input
 * THIS analysis used changed" — the same rule `analysisInputMatchesLiveSite`
 * already applies to typed preferences — which requires section-granular
 * identity.
 *
 * Each requested section's value is normalized with the SAME recursive
 * volatile-stripping as the whole-document hash (provenance timestamps and
 * `lastUpdated` removed), so a rebuild that re-stamps provenance without
 * changing evidence keeps every section hash stable. An ABSENT section hashes
 * as `null` — deliberately, so a section APPEARING later (new evidence for a
 * bound section) changes its hash and correctly stales analyses bound to it.
 *
 * Top-level exclusions do not apply here: sections are addressed by name, and
 * the excluded top-level keys (id/version/caches) are never section ids.
 */
export function computeSedSectionContentHashesV2(
  sedDocument: unknown,
  sectionIds: readonly string[],
): Record<string, string> {
  const source = (
    sedDocument !== null && typeof sedDocument === 'object' && !Array.isArray(sedDocument)
  ) ? sedDocument as Record<string, unknown> : {};
  const out: Record<string, string> = {};
  for (const sectionId of [...sectionIds].sort()) {
    const raw = source[sectionId];
    // Empty new ledgers carry the same evidence as a legacy absent ledger.
    const value = (SHARED_DOCUMENT_SED_INPUTS as readonly string[]).includes(sectionId)
      && Array.isArray(raw) && raw.length === 0 ? null : raw;
    out[sectionId] = hashCanonicalJson(
      value === undefined ? null : stripVolatile(value, false),
    );
  }
  return out;
}

/* ── Section identity scheme v3 (staleness metadata, never identity) ─────
 *
 * v2 section hashes change when a rebuild re-stamps CACHE or PROVENANCE
 * labels that carry no evidence, and every such change stales the analyses
 * bound to the section. Live 2026-09-30 (Goldenrod SED v10 → v11): grid,
 * electricity, connectivity and seismic changed only because their `source`
 * gained the " (cached)" provider-status suffix, which made the Screening
 * commit input-stale on four sections that carried identical data.
 *
 * v3 hashes each section under the v2 normalization PLUS these exclusions,
 * each a label that records HOW or WHEN evidence was obtained, not WHAT it is:
 *   - the " (cached)" provider-status suffix on `source` strings
 *     (siteEvidence.ts `section()`, dataProviders/cache.ts `withCache`);
 *   - boolean `cached` flags (`LinkedDocumentRef.cached`: the doc-match scorer
 *     read from its cache) and cache-hit counters (`bodyCacheHits`,
 *     `ocrCacheHits` on the municipal archive audit);
 *   - the carry-forward stamp's time (`carriedForward.at`, re-stamped by every
 *     rebuild that carries the section; its `reason` stays);
 *   - on `publicSentiment` only, the scan's provenance ledger:
 *     `history`, `change` and `refreshedFromSedId` (bookkeeping about earlier
 *     scans), `discoveryExclusions` (records read and ruled out of scope,
 *     retained in the SED as a coverage ledger, never evidence of a stance)
 *     and the scan grade (`queryContext.mode` and the mode-bearing
 *     `queryContext.fingerprint`; the rest of the query scope stays, and the
 *     inputs the fingerprint digests are bound separately: intent, location
 *     via `collectionContext`, preferences). Entries, assessment, coverage,
 *     tier-1 facts, per-scope/per-tech aggregates, status, reason, queries,
 *     attempted sources, leads and the collection receipt all stay.
 *
 * VERSIONED, so existing sites do not all go stale at once. v2 is unchanged
 * and still written everywhere. Analyses bind v3 BESIDE v2
 * (`sed.sectionHashesV3`); SED writers stamp a v3 mirror BESIDE the v2 one
 * (`sedSectionContentHashesV3`, valid only while it names the exact v2 map
 * written with it). A bound section is unchanged when ANY available pairing
 * of the two schemes matches (`boundSectionUnchanged`): equal hashes under any
 * mix of v2 and v3 imply equal v3 payloads, so the rule is never looser than
 * v3, and because v2↔v2 still counts it never stales anything v2 kept
 * current. Old analyses (v2 only) and old mirrors (v2 only) therefore keep
 * their v2 verdicts, gaining v3 currency only where a v2 hash proves it.
 */
export const SED_SECTION_IDENTITY_SCHEME_V3 = 'sed-section-content-v3' as const;

const CACHED_SOURCE_SUFFIX = /\s*\(cached\)\s*$/i;
const CACHE_COUNTER_KEYS = new Set(['bodyCacheHits', 'ocrCacheHits']);
const PUBLIC_SENTIMENT_PROVENANCE_KEYS = new Set(['history', 'change', 'refreshedFromSedId', 'discoveryExclusions']);
const PUBLIC_SENTIMENT_QUERY_PROVENANCE_KEYS = new Set(['mode', 'fingerprint']);

function stripVolatileV3(value: unknown, parentKey: string | null): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((entry) => stripVolatileV3(entry, null));
  }
  const source = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    if (isProvenanceTimestampKey(key)) continue;
    const entry = source[key];
    if (key === 'cached' && typeof entry === 'boolean') continue;
    if (CACHE_COUNTER_KEYS.has(key)) continue;
    if (parentKey === 'carriedForward' && key === 'at') continue;
    if (key === 'source' && typeof entry === 'string') {
      result[key] = entry.replace(CACHED_SOURCE_SUFFIX, '');
      continue;
    }
    result[key] = stripVolatileV3(entry, key);
  }
  return result;
}

/** The public-sentiment section minus its scan-provenance ledger (see above). */
function publicSentimentEvidenceProjection(section: unknown): unknown {
  if (section === null || typeof section !== 'object' || Array.isArray(section)) return section;
  const result: Record<string, unknown> = Object.create(null);
  for (const [key, value] of Object.entries(section as Record<string, unknown>)) {
    if (PUBLIC_SENTIMENT_PROVENANCE_KEYS.has(key)) continue;
    if (key === 'queryContext' && value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = Object.fromEntries(Object.entries(value as Record<string, unknown>)
        .filter(([queryKey]) => !PUBLIC_SENTIMENT_QUERY_PROVENANCE_KEYS.has(queryKey)));
      continue;
    }
    result[key] = value;
  }
  return result;
}

function sectionIdentityPayloadV3(sectionId: string, value: unknown): unknown {
  const projected = sectionId === 'publicSentiment' ? publicSentimentEvidenceProjection(value) : value;
  return projected === undefined ? null : stripVolatileV3(projected, null);
}

/**
 * THE evidence identity of a public-sentiment scan: its v3 section hash. Two
 * scans with equal values carry the same evidence (entries, assessment,
 * coverage, tier-1 facts, aggregates, status, leads, receipts), whatever their
 * scan dates, grades or history. The Public Sentiment refresh keeps the prior
 * SED version when a rescan reproduces it.
 */
export function publicSentimentEvidenceHash(section: unknown): string {
  return hashCanonicalJson(sectionIdentityPayloadV3('publicSentiment', section));
}

/** Per-section hashes under scheme v3 (same section set rules as v2). */
export function computeSedSectionContentHashesV3(
  sedDocument: unknown,
  sectionIds: readonly string[],
): Record<string, string> {
  const source = (
    sedDocument !== null && typeof sedDocument === 'object' && !Array.isArray(sedDocument)
  ) ? sedDocument as Record<string, unknown> : {};
  const out: Record<string, string> = {};
  for (const sectionId of [...sectionIds].sort()) {
    const raw = source[sectionId];
    const value = (SHARED_DOCUMENT_SED_INPUTS as readonly string[]).includes(sectionId)
      && Array.isArray(raw) && raw.length === 0 ? null : raw;
    out[sectionId] = hashCanonicalJson(sectionIdentityPayloadV3(sectionId, value));
  }
  return out;
}

/**
 * The site-doc v3 mirror. `v2Digest` binds it to the exact v2 map written in
 * the same update: a writer that restamps only the v2 map (an older browser
 * bundle, a rollback) leaves the digest stale, and the v3 mirror is ignored.
 */
export interface SedSectionContentHashesV3Mirror {
  scheme: typeof SED_SECTION_IDENTITY_SCHEME_V3;
  v2Digest: string;
  hashes: Record<string, string>;
}

/**
 * Both section mirrors for the exact SED bytes a writer persists. Every SED
 * writer stamps these two fields together (with `sedContentHash` and
 * `sedContentHashVersion`, as before).
 */
export function computeSedSectionHashMirrors(sedDocument: unknown): {
  sedSectionContentHashes: Record<string, string>;
  sedSectionContentHashesV3: SedSectionContentHashesV3Mirror;
  sedReportMaterialHashes: SedReportMaterialHashMirror;
} {
  const sections = allBoundSedSections();
  const sedSectionContentHashes = computeSedSectionContentHashesV2(sedDocument, sections);
  return {
    sedSectionContentHashes,
    sedReportMaterialHashes: computeSedReportMaterialHashMirror(sedDocument),
    sedSectionContentHashesV3: {
      scheme: SED_SECTION_IDENTITY_SCHEME_V3,
      v2Digest: hashCanonicalJson(sedSectionContentHashes),
      hashes: computeSedSectionContentHashesV3(sedDocument, sections),
    },
  };
}

/** The v3 mirror's hashes when it provably describes the same bytes as the v2 map; otherwise undefined. */
export function validSedSectionContentHashesV3(site: {
  sedSectionContentHashes?: Readonly<Record<string, string>>;
  sedSectionContentHashesV3?: unknown;
}): Readonly<Record<string, string>> | undefined {
  const mirror = site.sedSectionContentHashesV3;
  if (!mirror || typeof mirror !== 'object' || Array.isArray(mirror) || !site.sedSectionContentHashes) return undefined;
  const row = mirror as Partial<SedSectionContentHashesV3Mirror>;
  if (row.scheme !== SED_SECTION_IDENTITY_SCHEME_V3 || typeof row.v2Digest !== 'string'
    || row.v2Digest !== hashCanonicalJson(site.sedSectionContentHashes)
    || !row.hashes || typeof row.hashes !== 'object' || Array.isArray(row.hashes)) return undefined;
  return row.hashes;
}

/** A side's section hashes under the schemes it carries. */
export interface SectionHashSchemes {
  v2?: Readonly<Record<string, string>>;
  v3?: Readonly<Record<string, string>>;
}

/**
 * Is `sectionId` unchanged between a frozen binding and the live side? True
 * when ANY hash the frozen side carries equals ANY hash the live side carries
 * (see the scheme notes above): v2↔v2 keeps every v2 verdict; a v3 or mixed
 * match proves equal evidence under v3. A section absent from every map on
 * either side is not proven unchanged.
 */
export function boundSectionUnchanged(
  sectionId: string,
  frozen: SectionHashSchemes,
  live: SectionHashSchemes,
): boolean {
  const own = (map: Readonly<Record<string, string>> | undefined): string | undefined =>
    map && Object.prototype.hasOwnProperty.call(map, sectionId) ? map[sectionId] : undefined;
  const frozenHashes = [own(frozen.v2), own(frozen.v3)].filter((hash): hash is string => typeof hash === 'string');
  const liveHashes = [own(live.v2), own(live.v3)].filter((hash): hash is string => typeof hash === 'string');
  return frozenHashes.some(hash => liveHashes.includes(hash));
}

/**
 * The SED sections a decision domain's analysis binds for currency.
 *
 * UNION of the strict per-type map (`REPORT_TYPE_SED_REQUIREMENTS` — what the
 * type provably reads) and the registry's looser `requiredSedSections`
 * (documented in places as a SUPERSET of strict). Union is the safe
 * direction: over-binding can only cause a spurious re-run; under-binding
 * would let an analysis stay "current" while evidence it consumed changed —
 * a false-current, which this module must never produce. An empty resolution
 * (unknown domain) returns [] and callers fall back to whole-document
 * comparison.
 */
export function boundSedSectionsForDomain(domain: string): string[] {
  const strict = (REPORT_TYPE_SED_REQUIREMENTS as Record<string, readonly string[]>)[domain] ?? [];
  const currency = (REPORT_TYPE_SED_CURRENCY_BINDINGS as Record<string, readonly string[]>)[domain] ?? [];
  const registry = REPORT_TYPE_REGISTRY[domain as ReportTypeId]?.requiredSedSections ?? [];
  const shared = domain in REPORT_TYPE_REGISTRY ? SHARED_DOCUMENT_SED_INPUTS : [];
  return [...new Set([...strict, ...currency, ...registry, ...shared])].sort();
}

/** Legacy snapshots without ledger hashes represent an empty source ledger. */
export function sharedDocumentEvidenceMatches(
  frozen: Readonly<Record<string, string>> | undefined,
  live: Readonly<Record<string, string>> | undefined,
): boolean {
  const empty = hashCanonicalJson(null);
  return SHARED_DOCUMENT_SED_INPUTS.every(key => (frozen?.[key] ?? empty) === (live?.[key] ?? empty));
}

/**
 * Every section ANY decision domain binds — the mirror-side universe.
 *
 * The site-doc mirror (`sedSectionContentHashes`) must hash exactly this set:
 * an analysis binds `boundSedSectionsForDomain(domain)` hashes where an
 * ABSENT section hashes as `null`, so the mirror must carry the same
 * absent-as-null hash for every potentially-bound section. Stamping only the
 * sections PRESENT in the SED would make a domain that binds a not-yet-
 * present section (public-sentiment before its SED section first lands) read
 * permanently stale: bound hash(null) vs missing mirror key ⇒ fail-closed
 * forever.
 */
export function allBoundSedSections(): string[] {
  const out = new Set<string>();
  for (const domain of Object.keys(REPORT_TYPE_SED_REQUIREMENTS)) {
    for (const sectionId of boundSedSectionsForDomain(domain)) out.add(sectionId);
  }
  return [...out].sort();
}
